"use client"

import { useEffect, useState } from "react"
import type { BooruPost } from "@/lib/booru-api"
import { NavigationBar } from "./navigation-bar"
import { BooruGrid } from "./booru-grid"
import { Loader2, AlertCircle } from "@/lib/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useSourceStore } from "@/lib/source-store"
import { DoujinsPremiumModal } from "@/components/doujins-premium-modal"
import { useTranslations } from "next-intl"
import { mayShow } from "@/lib/platform-screen"

interface AlbumGridViewProps {
  album: BooruPost
  onBack: () => void
  onPostClick?: (post: BooruPost) => void
  categoryName?: string // Category context from navigation stack
}

/**
 * Hitomi albums: the album's page list is fetched when the album is opened (search results carry only the gallery id), and
 * Hitomi's file-address resolver (gg.js, about 20 KB) is only re-fetched every 15 minutes instead of on every open. Fetched albums
 * are kept for the session, so reopening one is instant. The resolver changes periodically, hence the 15 minutes.
 */
const HITOMI_SYNC_MS = 15 * 60 * 1000
let hitomiSyncedAt = 0
let hitomiSyncing: Promise<void> | null = null
const hitomiGalleries = new Map<number, any>()
const hitomiGalleriesLoading = new Map<number, Promise<any>>()

async function syncHitomiResolver(hitomi: any): Promise<void> {
  if (Date.now() - hitomiSyncedAt < HITOMI_SYNC_MS) return
  if (!hitomiSyncing) {
    hitomiSyncing = Promise.resolve(hitomi.ImageUriResolver.synchronize())
      .then(() => { hitomiSyncedAt = Date.now() })
      .finally(() => { hitomiSyncing = null })
  }
  await hitomiSyncing
}

/** A Hitomi gallery's own tags as "type:name" strings, for the platform tags check. */
function hitomiGalleryTags(gallery: any): string[] {
  return Array.isArray(gallery?.tags) ? gallery.tags.map((t: any) => (typeof t === "string" ? t : `${t?.type ?? ""}:${t?.name ?? ""}`)) : []
}

async function loadHitomiGallery(hitomi: any, galleryId: number): Promise<any> {
  const known = hitomiGalleries.get(galleryId)
  if (known) return known
  // Two opens of the same album at the same moment (React runs effects twice in development) share one request.
  let loading = hitomiGalleriesLoading.get(galleryId)
  if (!loading) {
    loading = Promise.resolve(hitomi.getGallery(galleryId))
      .then((gallery: any) => {
        if (gallery?.files?.length) hitomiGalleries.set(galleryId, gallery)
        return gallery
      })
      .finally(() => hitomiGalleriesLoading.delete(galleryId))
    hitomiGalleriesLoading.set(galleryId, loading)
  }
  return loading
}

export function AlbumGridView({ album, onBack, onPostClick, categoryName }: AlbumGridViewProps) {
  const t = useTranslations("albumGridView")
  console.log('╔════════════════════════════════════════════════════════════════════════════╗')
  console.log('║                      ALBUM GRID VIEW - RENDER                              ║')
  console.log('╚════════════════════════════════════════════════════════════════════════════╝')
  console.log('[AlbumGridView] 🎨 Component rendering')
  console.log('  • album.title:', album.title)
  console.log('  • album.id:', album.id)
  console.log('  • album.site:', album.site)
  console.log('  • album.type:', album.type)
  console.log('  • album.postUrl:', album.postUrl)
  console.log('  • album.rawPost:', album.rawPost)
  console.log('  • album.rawPost?.isCategory:', album.rawPost?.isCategory)
  console.log('  • album.rawPost?.galleryUrl:', album.rawPost?.galleryUrl)
  console.log('  • album.rawPost?.originalUrl:', album.rawPost?.originalUrl)
  console.log('  • categoryName prop:', categoryName)
  console.log('  • Full album object:', JSON.stringify(album, null, 2))
  console.log('╚════════════════════════════════════════════════════════════════════════════╝')
  console.log('')
  
  const [albumPosts, setAlbumPosts] = useState<BooruPost[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [showPremiumModal, setShowPremiumModal] = useState(false)
  const [galleryData, setGalleryData] = useState<any>(null)
  
  // Get source store for 4chan thumbnail quality setting
  const { getSource } = useSourceStore()
  
  // Load more galleries when user clicks button or scrolls to bottom
  const loadMore = async () => {
    if (isLoadingMore || !hasMore) return
    
    // Only load more for category views
    if (!album.rawPost?.isCategory && !album.rawPost?.galleryUrl?.startsWith('category:')) {
      return
    }
    
    setIsLoadingMore(true)
    try {
      const nextPage = currentPage + 1
      await loadCategoryGalleries(nextPage, true)
      setCurrentPage(nextPage)
    } catch (error) {
      console.error('[AlbumGridView] Failed to load more:', error)
    } finally {
      setIsLoadingMore(false)
    }
  }
  
  // Function to load category galleries (supports pagination)
  const loadCategoryGalleries = async (page: number, append: boolean = false) => {
    if (!album.rawPost?.galleryUrl) return
    
    const categoryName = album.rawPost.galleryUrl.replace('category:', '')
    console.log(`[v0] Loading Doujins category: ${categoryName}, page: ${page}`)
    
    // Determine the category type and construct the proper API URL
    let apiUrl: string
    
    // Check if this is an artist or series sub-category
    if (categoryName.includes('/')) {
      // This is a sub-category like "artists/otochichi" or "series/some-series"
      const [categoryType, categoryValue] = categoryName.split('/')
      
      // Check if categoryValue is a full series URL (like "original-doujins-series-93083")
      if (categoryValue.includes('-series-') || categoryValue.match(/^\d+$/)) {
        // This is a direct series page URL, fetch from the actual page
        apiUrl = `/api/booru/doujins/search?query=&page=${page}&category=series&subcategory=${encodeURIComponent(categoryValue)}`
        console.log(`[v0] Loading series collection page: ${categoryValue}`)
      } else {
        // This is a series/artist category listing
        apiUrl = `/api/booru/doujins/search?query=&page=${page}&category=${categoryType}&subcategory=${encodeURIComponent(categoryValue)}`
        console.log(`[v0] Loading ${categoryType} sub-category: ${categoryValue}`)
      }
    } else {
      // This is a top-level category like "popular", "artists", etc.
      apiUrl = `/api/booru/doujins/search?query=&page=${page}&category=${encodeURIComponent(categoryName)}`
    }
    
    try {
      // Fetch galleries from this category
      const response = await fetch(apiUrl)
      if (!response.ok) throw new Error(t("failedToFetchCategory", { status: response.status }))
      
      const data = await response.json()
      console.log(`[v0] Loaded ${data.galleries.length} galleries from category ${categoryName}, page ${page}`)
      
      // Check if there are more pages
      if (data.galleries.length === 0) {
        setHasMore(false)
        return []
      }
      
      // Map galleries to BooruPost format
      const posts: BooruPost[] = data.galleries.map((gallery: any) => {
        return {
          id: gallery.id,
          site: "doujins",
          fileUrl: gallery.coverUrl,
          previewUrl: gallery.coverUrl,
          sampleUrl: gallery.coverUrl,
          tags: gallery.tags || [],
          rating: 'explicit' as const,
          score: 0,
          type: gallery.isCategory ? 'album' as const : 'album' as const,
          albumMediaCount: gallery.pageCount,
          title: gallery.title,
          rawPost: {
            galleryUrl: gallery.galleryUrl,
            galleryId: gallery.id,
            isCategory: gallery.isCategory,
            hasAudio: gallery.hasAudio
          }
        }
      })
      
      if (append) {
        setAlbumPosts(prev => {
          // Deduplicate by ID to prevent duplicate key errors
          const existingIds = new Set(prev.map(p => p.id))
          const newPosts = posts.filter(p => !existingIds.has(p.id))
          
          if (newPosts.length === 0) {
            console.log('[AlbumGridView] No new posts to add (all duplicates)')
            setHasMore(false) // No more unique posts, stop pagination
            return prev
          }
          
          console.log(`[AlbumGridView] Adding ${newPosts.length} new posts (filtered ${posts.length - newPosts.length} duplicates)`)
          return [...prev, ...newPosts]
        })
      } else {
        setAlbumPosts(posts)
      }
      
      return posts
    } catch (error) {
      console.error(`[v0] Failed to load category:`, error)
      throw error
    }
  }

  useEffect(() => {
    console.log('[AlbumGridView] Album changed:', {
      id: album.id,
      title: album.title,
      site: album.site,
      type: album.type,
      postUrl: album.postUrl,
      isCategory: album.rawPost?.isCategory,
      galleryUrl: album.rawPost?.galleryUrl,
      rawPost: album.rawPost
    })
    
    // Reset pagination when album changes
    setCurrentPage(1)
    setHasMore(true)
    setAlbumPosts([])
    
    // Load album content
    const loadAlbumContent = async () => {
      try {
        setLoading(true)
        setError(null)

        // Platform tags (lib/platform-screen.ts): an album can be opened from a link or a saved record, not only from a search that
        // was already screened, so check it here. Fails closed.
        if (!(await mayShow({ id: album.id, site: album.site, tags: album.tags, title: (album as { title?: string }).title }))) {
          setAlbumPosts([])
          return
        }

        // If album has albumImages array, convert to posts
        if (album.albumImages && album.albumImages.length > 0) {
          const posts: BooruPost[] = album.albumImages.map((imageUrl, index) => ({
            id: `${album.id}-${index}`,
            site: album.site,
            previewUrl: imageUrl,
            fileUrl: imageUrl,
            sampleUrl: imageUrl,
            tags: album.tags || [],
            score: album.score || 0,
            rating: album.rating || "safe",
            width: album.width || 0,
            height: album.height || 0,
            type: "image",
          }))
          setAlbumPosts(posts)
        } else if (album.site === "hitomi" && album.rawPost?.gallery) {
          // For Hitomi.la, use node-hitomi's ImageUriResolver
          const hitomi = (await import('node-hitomi')).default
          const gallery = album.rawPost.gallery
          if (!(await mayShow({ id: gallery.id, site: "hitomi", tags: hitomiGalleryTags(gallery) }))) {
            setAlbumPosts([])
            return
          }
          
          console.log(`[v0] Loading Hitomi gallery ${gallery.id} with ${gallery.files.length} pages`)
          
          // Handle missing gallery data
          if (!gallery.files || !Array.isArray(gallery.files) || gallery.files.length === 0) {
            console.warn(`[v0] Hitomi gallery ${gallery.id} has no files`)
            setAlbumPosts([])
            return
          }
          
          // Synchronize ImageUriResolver
          await syncHitomiResolver(hitomi)
          
          const posts: BooruPost[] = gallery.files.map((file: any, index: number) => {
            // Try formats in order of preference: webp > avif > jxl
            let imageUrl: string
            try {
              imageUrl = hitomi.ImageUriResolver.getImageUri(file, 'webp', {
                isThumbnail: false, // Full size for album view
              })
            } catch (error) {
              try {
                imageUrl = hitomi.ImageUriResolver.getImageUri(file, 'avif', {
                  isThumbnail: false,
                })
              } catch (error2) {
                // Fall back to jxl
                imageUrl = hitomi.ImageUriResolver.getImageUri(file, 'jxl', {
                  isThumbnail: false,
                })
              }
            }
            
            // Ensure URL has protocol (node-hitomi may return URLs without https://)
            if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
              imageUrl = `https://${imageUrl}`
            }
            
            return {
              id: `${gallery.id}-${index}`,
              site: "hitomi",
              previewUrl: imageUrl,
              fileUrl: imageUrl,
              sampleUrl: imageUrl,
              tags: album.tags || [], // Inherit tags from parent gallery
              score: 0,
              rating: "explicit",
              width: file.width || 0,
              height: file.height || 0,
              type: "image",
            }
          })
          
          // Create albumImages array with all image URLs in order
          const albumImages = posts.map(p => p.fileUrl)
          
          // Add albumImages to each post so modal navigation works
          const postsWithAlbumImages = posts.map(p => ({
            ...p,
            albumImages: albumImages,
          }))
          
          console.log(`[v0] Loaded ${postsWithAlbumImages.length} pages from Hitomi gallery ${gallery.id}`)
          setAlbumPosts(postsWithAlbumImages)
        } else if (album.site === "hitomi" && (album.rawPost?.galleryId || album.rawPost?.galleryInfo)) {
          // The normal path for Hitomi results: they carry only the gallery id (the page list is fetched here, when the album is opened).
          // It is also the path for old saved/bookmarked records that only have `galleryInfo` (no full `gallery` object).
          // This used to reconstruct image URLs itself via HitomiURLConstructor, a hand-rolled
          // guess at Hitomi's subdomain-assignment scheme (static id%3 → a/b/c) — Hitomi's real
          // scheme is a hash of the filename, and changes periodically. Roughly a third to two
          // thirds of URLs it built 404'd, which showed up as "Content removed from source" for
          // galleries that were completely fine. Fetch the real gallery data by ID instead and
          // reuse the same correct node-hitomi resolver path as the primary branch above.
          const galleryId = Number(album.rawPost.galleryId || album.id)
          console.log(`[v0] Legacy Hitomi record (no rawPost.gallery) — refetching gallery ${galleryId} via node-hitomi`)

          const hitomi = (await import('node-hitomi')).default
          const gallery = await loadHitomiGallery(hitomi, galleryId)
          // The search result carried only the id, so its tags may be missing: check the gallery's own tags too.
          if (gallery && !(await mayShow({ id: galleryId, site: "hitomi", tags: hitomiGalleryTags(gallery) }))) {
            setAlbumPosts([])
            return
          }

          if (!gallery?.files || !Array.isArray(gallery.files) || gallery.files.length === 0) {
            console.warn(`[v0] Hitomi gallery ${galleryId} has no files`)
            setAlbumPosts([])
            return
          }

          await syncHitomiResolver(hitomi)

          const posts: BooruPost[] = gallery.files.map((file: any, index: number) => {
            let imageUrl: string
            try {
              imageUrl = hitomi.ImageUriResolver.getImageUri(file, 'webp', { isThumbnail: false })
            } catch {
              try {
                imageUrl = hitomi.ImageUriResolver.getImageUri(file, 'avif', { isThumbnail: false })
              } catch {
                imageUrl = hitomi.ImageUriResolver.getImageUri(file, 'jxl', { isThumbnail: false })
              }
            }
            if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
              imageUrl = `https://${imageUrl}`
            }

            return {
              id: `${galleryId}-${index}`,
              site: "hitomi",
              previewUrl: imageUrl,
              fileUrl: imageUrl,
              sampleUrl: imageUrl,
              tags: album.tags || [], // Inherit tags from parent gallery
              score: 0,
              rating: "explicit",
              width: file.width || 0,
              height: file.height || 0,
              type: "image",
            }
          })

          const albumImages = posts.map(p => p.fileUrl)
          const postsWithAlbumImages = posts.map(p => ({ ...p, albumImages }))

          console.log(`[v0] Loaded ${postsWithAlbumImages.length} pages from Hitomi gallery ${galleryId} (refetched)`)
          setAlbumPosts(postsWithAlbumImages)
        } else if (album.site === "xlecx" && (album.rawPost?.galleryUrl || album.postUrl)) {
          // For XlecX galleries, fetch gallery pages via API
          // Fallback to postUrl if galleryUrl is not in rawPost
          const galleryUrl = album.rawPost?.galleryUrl || album.postUrl || ''
          
          if (!galleryUrl) {
            console.error(`[v0] XlecX album has no gallery URL!`)
            setError(t("noGalleryURLAvailable"))
            setAlbumPosts([])
            return
          }
          
          console.log(`[v0] ========== XLECX GALLERY DEBUG ==========`)
          console.log(`[v0] Loading XlecX gallery from: ${galleryUrl}`)
          console.log(`[v0] Album ID: ${album.id}`)
          console.log(`[v0] Album title: ${album.title}`)
          console.log(`[v0] Album rawPost:`, album.rawPost)
          console.log(`[v0] Album postUrl:`, album.postUrl)
          console.log(`[v0] Using galleryUrl:`, galleryUrl)
          console.log(`[v0] ==========================================`)
          
          try {
            const response = await fetch(`/api/booru/xlecx/gallery?url=${encodeURIComponent(galleryUrl)}`)
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}))
              console.error(`[v0] XlecX Gallery API error:`, errorData)
              throw new Error(t("failedToFetchGallery", { status: response.status }))
            }
            const galleryDetail = await response.json()
            
            console.log(`[v0] ========== XLECX GALLERY API RESPONSE ==========`)
            console.log(`[v0] Pages count: ${galleryDetail.pages.length}`)
            console.log(`[v0] Has video: ${galleryDetail.hasVideo}`)
            console.log(`[v0] Tags: ${galleryDetail.tags.length}`)
            console.log(`[v0] Title: ${galleryDetail.title}`)
            console.log(`[v0] First page:`, galleryDetail.pages[0])
            console.log(`[v0] ==========================================`)
            
            const posts: BooruPost[] = galleryDetail.pages.map((page: any, index: number) => {
              return {
                id: `${album.id}-${index}`,
                site: "xlecx",
                previewUrl: page.imageUrl,
                fileUrl: page.imageUrl,
                sampleUrl: page.imageUrl,
                tags: galleryDetail.tags || album.tags || [],
                score: 0,
                rating: "explicit",
                type: page.isVideo ? "video" : "image",
                rawPost: {
                  isVideo: page.isVideo,
                  galleryUrl: album.rawPost?.galleryUrl,
                  galleryId: album.id
                }
              }
            })
            
            // Create albumImages array with all image URLs in order (same as Hitomi/Doujins)
            const albumImages = posts.map(p => p.fileUrl)
            
            // Add albumImages to each post so modal navigation works
            const postsWithAlbumImages = posts.map(p => ({
              ...p,
              albumImages: albumImages,
            }))
            
            console.log(`[v0] Loaded ${postsWithAlbumImages.length} pages from XlecX gallery with navigation support`)
            setAlbumPosts(postsWithAlbumImages)
          } catch (error) {
            console.error(`[v0] Failed to load XlecX gallery:`, error)
            setError(error instanceof Error ? error.message : 'Failed to load gallery')
            setAlbumPosts([])
          }
        } else if (album.site === "doujins" && album.rawPost?.galleryUrl) {
          // Check if this is a category (board-like navigation)
          if (album.rawPost.isCategory || album.rawPost.galleryUrl.startsWith('category:')) {
            await loadCategoryGalleries(1, false)
            return
          }
          
          // For Doujins.com galleries, fetch gallery pages via API
          const galleryUrl = album.rawPost.galleryUrl
          
          console.log(`[v0] ========== DOUJINS GALLERY DEBUG ==========`)
          console.log(`[v0] Loading Doujins gallery from: ${galleryUrl}`)
          console.log(`[v0] Album ID: ${album.id}`)
          console.log(`[v0] Album title: ${album.title}`)
          console.log(`[v0] Album rawPost:`, album.rawPost)
          console.log(`[v0] Is category:`, album.rawPost.isCategory)
          console.log(`[v0] Full album object:`, album)
          console.log(`[v0] ==========================================`)
          
          try {
            // Import DoujinsClient dynamically
            const { default: DoujinsClient } = await import('../lib/booru-api').then(mod => ({
              default: class DoujinsClient {
                async getGalleryPages(url: string) {
                  console.log(`[v0] Fetching gallery from API: /api/booru/doujins/gallery?url=${encodeURIComponent(url)}`)
                  const response = await fetch(`/api/booru/doujins/gallery?url=${encodeURIComponent(url)}`)
                  if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}))
                    console.error(`[v0] Gallery API error:`, errorData)
                    if (errorData.requiresPremium) {
                      throw new Error(t("thisGalleryRequiresAPremiumSubscriptionT"))
                    }
                    throw new Error(t("failedToFetchGallery", { status: response.status }))
                  }
                  const data = await response.json()
                  console.log(`[v0] Gallery API response:`, data)
                  return data
                }
              }
            }))
            
            const doujinsClient = new DoujinsClient()
            const galleryDetail = await doujinsClient.getGalleryPages(galleryUrl)

            // The grid card carries no tags; the gallery's real tags arrive with its pages. Check them now (platform tags,
            // lib/platform-screen.ts), as Hitomi does, so a gallery with a blocked tag does not open. Fails closed.
            if (!(await mayShow({ id: album.id, site: "doujins", tags: galleryDetail.tags || [], title: galleryDetail.title }))) {
              setAlbumPosts([])
              return
            }

            console.log(`[v0] ========== GALLERY API RESPONSE ==========`)
            console.log(`[v0] Pages count: ${galleryDetail.pages.length}`)
            console.log(`[v0] Has audio: ${galleryDetail.hasAudio}`)
            console.log(`[v0] Tags: ${galleryDetail.tags.length}`)
            console.log(`[v0] Title: ${galleryDetail.title}`)
            console.log(`[v0] First page:`, galleryDetail.pages[0])
            console.log(`[v0] Full gallery detail:`, galleryDetail)
            console.log(`[v0] ==========================================`)
            
            // Store gallery data for premium detection
            setGalleryData(galleryDetail)
            
            // Check if this is a premium gallery and show modal
            const isPremiumGallery = galleryDetail.isPremium || galleryDetail.pageCount === 1 || galleryDetail.pages.length === 1
            console.log(`[AlbumGridView v49] Premium detection:`, {
              isPremium: galleryDetail.isPremium,
              pageCount: galleryDetail.pageCount,
              pagesLength: galleryDetail.pages.length,
              willShowModal: isPremiumGallery
            })
            
            if (isPremiumGallery) {
              // Show premium modal after a short delay
              setTimeout(() => {
                console.log(`[AlbumGridView v49] AUTO-SHOWING PREMIUM MODAL`)
                setShowPremiumModal(true)
              }, 500)
            }
            
            const posts: BooruPost[] = galleryDetail.pages.map((page: any, index: number) => {
              return {
                id: `${album.id}-${index}`,
                site: "doujins",
                previewUrl: page.imageUrl,
                fileUrl: page.imageUrl,
                sampleUrl: page.imageUrl,
                tags: galleryDetail.tags || album.tags || [],
                score: 0,
                rating: "explicit",
                type: "image",
                rawPost: {
                  audioUrl: page.audioUrl,
                  hasAudio: !!page.audioUrl,
                  galleryUrl: album.rawPost?.galleryUrl, // Preserve gallery URL for modal navigation
                  galleryId: album.id
                }
              }
            })
            
            setAlbumPosts(posts)
          } catch (error) {
            console.error(`[v0] Failed to load Doujins gallery:`, error)
            setError(error instanceof Error ? error.message : 'Failed to load gallery')
            setAlbumPosts([])
          }
        } else if (album.site === "hentaiera" && album.rawPost?.galleryUrl) {
          // Category tiles (Manga/Doujinshi/Western/etc.) - single-level, no sub-categories
          if (album.rawPost.isCategory || album.rawPost.galleryUrl.startsWith('category:')) {
            const categorySlug = album.rawPost.galleryUrl.replace('category:', '')
            try {
              const response = await fetch(`/api/booru/hentaiera/search?query=&page=1&category=${encodeURIComponent(categorySlug)}`)
              if (!response.ok) throw new Error(t("failedToFetchCategory", { status: response.status }))
              const data = await response.json()

              const posts: BooruPost[] = data.galleries.map((gallery: any) => ({
                id: gallery.id,
                site: "hentaiera",
                fileUrl: gallery.coverUrl,
                previewUrl: gallery.coverUrl,
                sampleUrl: gallery.coverUrl,
                tags: gallery.tags || [],
                rating: 'explicit' as const,
                score: 0,
                type: 'album' as const,
                albumMediaCount: gallery.pageCount,
                title: gallery.title,
                rawPost: {
                  galleryUrl: gallery.galleryUrl,
                  galleryId: gallery.id,
                  isCategory: gallery.isCategory,
                },
              }))

              setAlbumPosts(posts)
            } catch (error) {
              console.error(`[v0] Failed to load HentaiEra category:`, error)
              setError(error instanceof Error ? error.message : 'Failed to load category')
              setAlbumPosts([])
            }
            return
          }

          // Real gallery - fetch its pages
          const galleryUrl = album.rawPost.galleryUrl

          try {
            // v=2: the page list changed (each page's real file type); browsers hold the route's answers for an hour, so older lists are skipped.
            const response = await fetch(`/api/booru/hentaiera/gallery?url=${encodeURIComponent(galleryUrl)}&v=2`)
            if (!response.ok) {
              throw new Error(t("failedToFetchGallery", { status: response.status }))
            }
            const galleryDetail = await response.json()

            // The grid card carries only the category tag; the gallery's real tags arrive with its pages. Check them now (platform tags,
            // lib/platform-screen.ts), as Hitomi does, or a gallery tagged e.g. "lolicon" opens despite the filters. Fails closed.
            if (!(await mayShow({ id: album.id, site: "hentaiera", tags: galleryDetail.tags || [], title: galleryDetail.title }))) {
              setAlbumPosts([])
              return
            }

            const posts: BooruPost[] = galleryDetail.pages.map((page: any, index: number) => ({
              id: `${album.id}-${index}`,
              site: "hentaiera",
              previewUrl: page.imageUrl,
              fileUrl: page.imageUrl,
              sampleUrl: page.imageUrl,
              tags: galleryDetail.tags || album.tags || [],
              score: 0,
              rating: "explicit",
              type: "image",
              rawPost: {
                galleryUrl: album.rawPost?.galleryUrl,
                galleryId: album.id,
              },
            }))

            setAlbumPosts(posts)
          } catch (error) {
            console.error(`[v0] Failed to load HentaiEra gallery:`, error)
            setError(error instanceof Error ? error.message : 'Failed to load gallery')
            setAlbumPosts([])
          }
        } else if (album.site === "lewdvault" && album.rawPost?.galleryUrl) {
          // Category tiles (Latest/Hot) - single-level
          if (album.rawPost.isCategory || album.rawPost.galleryUrl.startsWith('category:')) {
            const categorySlug = album.rawPost.galleryUrl.replace('category:', '')
            try {
              const response = await fetch(`/api/booru/lewdvault/search?query=&page=1&category=${encodeURIComponent(categorySlug)}`)
              if (!response.ok) throw new Error(t("failedToFetchCategory", { status: response.status }))
              const data = await response.json()

              const posts: BooruPost[] = data.galleries.map((gallery: any) => ({
                id: gallery.id,
                site: "lewdvault",
                fileUrl: gallery.coverUrl,
                previewUrl: gallery.coverUrl,
                sampleUrl: gallery.coverUrl,
                tags: gallery.tags || [],
                rating: 'explicit' as const,
                score: 0,
                type: 'album' as const,
                title: gallery.title,
                rawPost: {
                  galleryUrl: gallery.galleryUrl,
                  galleryId: gallery.id,
                  isCategory: gallery.isCategory,
                },
              }))

              setAlbumPosts(posts)
            } catch (error) {
              console.error(`[v0] Failed to load LewdVault category:`, error)
              setError(error instanceof Error ? error.message : 'Failed to load category')
              setAlbumPosts([])
            }
            return
          }

          // Real gallery - fetch its pages
          const galleryUrl = album.rawPost.galleryUrl

          try {
            const response = await fetch(`/api/booru/lewdvault/gallery?url=${encodeURIComponent(galleryUrl)}`)
            if (!response.ok) {
              throw new Error(t("failedToFetchGallery", { status: response.status }))
            }
            const galleryDetail = await response.json()

            const posts: BooruPost[] = galleryDetail.pages.map((page: any, index: number) => ({
              id: `${album.id}-${index}`,
              site: "lewdvault",
              previewUrl: page.imageUrl,
              fileUrl: page.imageUrl,
              sampleUrl: page.imageUrl,
              tags: galleryDetail.tags || album.tags || [],
              score: 0,
              rating: "explicit",
              type: "image",
              rawPost: {
                galleryUrl: album.rawPost?.galleryUrl,
                galleryId: album.id,
              },
            }))

            setAlbumPosts(posts)
          } catch (error) {
            console.error(`[v0] Failed to load LewdVault gallery:`, error)
            setError(error instanceof Error ? error.message : 'Failed to load gallery')
            setAlbumPosts([])
          }
        } else if (album.site === "4chan") {
          // For 4chan threads, fetch all images from the thread via our API
          const board = album.tags?.find(tag => tag.startsWith("board:"))?.replace("board:", "") || "gif"
          const threadNo = album.id
          
          const apiUrl = `/api/4chan/thread?board=${board}&thread=${threadNo}`
          console.log(`[v0] Fetching thread /${board}/${threadNo} via API: ${apiUrl}`)
          
          const response = await fetch(apiUrl)
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            throw new Error(errorData.error || `Failed to fetch thread: ${response.statusText}`)
          }
          
          const data = await response.json()
          
          // Get thumbnail quality setting from source config
          const source = getSource("4chan")
          const useLowQuality = source?.thumbnailQuality !== "full"
          
          const posts: BooruPost[] = data.posts
            .filter((post: any) => post.tim && post.ext) // Only posts with images
            .map((post: any) => {
              const ext = post.ext.toLowerCase()
              // All video formats including GIF
              const isVideo = /\.(webm|mp4|mov|avi|mkv|flv|wmv|m4v|3gp|ogv|gif|gifv)$/i.test(ext)
              
              // 4chan thumbnail sizes:
              // - {tim}s.jpg = 125x125 (only thumbnail size available)
              // User can choose between low quality (thumbnail) or full quality (original)
              
              const fullUrl = `https://i.4cdn.org/${board}/${post.tim}${post.ext}`
              const thumbUrl = `https://i.4cdn.org/${board}/${post.tim}s.jpg`
              
              return {
                id: `${threadNo}-${post.no}`,
                site: "4chan",
                // Use thumbnail or full based on user setting
                previewUrl: useLowQuality ? thumbUrl : fullUrl,
                fileUrl: fullUrl,
                sampleUrl: fullUrl,
                tags: [`board:${board}`, ...(post.com ? [post.com.substring(0, 100)] : [])],
                score: 0,
                rating: "explicit",
                width: post.w || 0,
                height: post.h || 0,
                type: isVideo ? "video" : "image",
              }
            })
          
          console.log(`[v0] Loaded ${posts.length} images from thread /${board}/${threadNo}`)
          setAlbumPosts(posts)
        } else {
          // No album content available
          setAlbumPosts([])
        }
      } catch (err) {
        console.error("[v0] Failed to load album content:", err)
        setError(err instanceof Error ? err.message : "Failed to load album content")
      } finally {
        setLoading(false)
      }
    }

    loadAlbumContent()
  }, [album])
  
  // Intersection observer for infinite scroll - MUST be after all other useEffects
  useEffect(() => {
    // Only set up observer for category views
    const isCategoryView = album.rawPost?.isCategory || album.rawPost?.galleryUrl?.startsWith('category:')
    if (!isCategoryView) return
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMore && hasMore) {
          loadMore()
        }
      },
      { rootMargin: '200px' }
    )
    
    const sentinel = document.getElementById('load-more-sentinel')
    if (sentinel) {
      observer.observe(sentinel)
    }
    
    return () => observer.disconnect()
  }, [isLoadingMore, hasMore, currentPage, album])

  // Get album title
  const getAlbumTitle = () => {
    if (album.site === "4chan") {
      const board = album.tags?.find(tag => tag.startsWith("board:"))?.replace("board:", "") || "unknown"
      return `Thread #${album.id} - /${board}/`
    }
    return album.tags?.[0] || `Album ${album.id}`
  }

  // Get source name
  const getSourceName = () => {
    if (album.site === "4chan") return "4chan"
    return album.site || "Unknown"
  }

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <NavigationBar
          sourceName={getSourceName()}
          albumTitle={getAlbumTitle()}
          categoryName={categoryName}
          onBack={onBack}
        />
        <div className="flex items-center justify-center flex-1 p-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <NavigationBar
          sourceName={getSourceName()}
          albumTitle={getAlbumTitle()}
          categoryName={categoryName}
          onBack={onBack}
        />
        <div className="flex items-center justify-center flex-1 p-8">
          <Alert variant="destructive" className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t("failedToLoadAlbum")}</AlertTitle>
            <AlertDescription className="flex flex-col gap-3">
              <p>{error}</p>
              <div className="flex gap-2">
                <Button onClick={onBack} variant="outline" size="sm">
                  {t("goBack")}
                </Button>
                <Button onClick={() => window.location.reload()} variant="outline" size="sm">
                  {t("retry")}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  if (albumPosts.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <NavigationBar
          sourceName={getSourceName()}
          albumTitle={getAlbumTitle()}
          categoryName={categoryName}
          onBack={onBack}
        />
        <div className="flex items-center justify-center flex-1 p-8">
          <Alert className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t("emptyAlbum")}</AlertTitle>
            <AlertDescription className="flex flex-col gap-3">
              <p>{t("thisAlbumContainsNoImages")}</p>
              <Button onClick={onBack} variant="outline" size="sm" className="w-fit">
                {t("goBack")}
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }
  
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <NavigationBar
        sourceName={getSourceName()}
        albumTitle={getAlbumTitle()}
        categoryName={categoryName}
        onBack={onBack}
      />
      <div className="flex-1 overflow-y-auto">
        <BooruGrid
          posts={albumPosts}
          onPostClick={onPostClick}
          isAlbumView={!album.rawPost?.isCategory && !album.rawPost?.galleryUrl?.startsWith('category:')}
          parentAlbum={album}
        />
        
        {/* Infinite scroll sentinel and load more button */}
        {(album.rawPost?.isCategory || album.rawPost?.galleryUrl?.startsWith('category:')) && (
          <div className="flex flex-col items-center gap-4 p-8">
            <div id="load-more-sentinel" className="h-1" />
            
            {isLoadingMore && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t("loadingMore")}</span>
              </div>
            )}
            
            {!isLoadingMore && hasMore && (
              <Button onClick={loadMore} variant="outline">
                {t("loadMore")}
              </Button>
            )}
            
            {!hasMore && albumPosts.length > 0 && (
              <p className="text-sm text-muted-foreground">{t("noMoreGalleriesToLoad")}</p>
            )}
          </div>
        )}
      </div>
      
      {/* Premium Modal */}
      <DoujinsPremiumModal isOpen={showPremiumModal} onClose={() => setShowPremiumModal(false)} />
    </div>
  )
}
